import { defineStore } from 'pinia'
import axios from '@/services/api/api'
import { aiAgentsLevel } from '@/utils/aiAgentsLabels'
import { canAccessSupport } from '@/utils/supportAccess'

// Promessa em voo de /users/me, compartilhada entre chamadores simultâneos.
let inFlight: Promise<void> | null = null

export const useUserStore = defineStore('user', {
  state: () => ({
    id: null,
    name: '',
    username: '',
    email: '',
    profile: '',
    isAdmin: false,
    supportLevel: null as string | null,
    // Papel no Easy AI Agents (none/viewer/reviewer/admin). Admin do HUB vale como admin.
    aiAgentsRole: 'none' as string,
    theme: 'light',
    twoFactorEnabled: false,
  }),
  getters: {
    isN3: (state) => state.supportLevel === 'n3',
    // Telas de plantão: admin ou quem tem um dos níveis pedidos.
    hasSupportAccess: (state) => (levels: readonly string[] | undefined) => canAccessSupport(levels, state),
    aiAgentsAtLeast: (state) => (role: string) =>
      state.isAdmin || aiAgentsLevel(state.aiAgentsRole) >= aiAgentsLevel(role),
  },
  actions: {
    clearUserData() {
      this.id = null
      this.name = ''
      this.username = ''
      this.email = ''
      this.profile = ''
      this.isAdmin = false
      this.supportLevel = null
      this.aiAgentsRole = 'none'
      this.theme = 'light'
      this.twoFactorEnabled = false
    },

    async fetchUserData() {
      // Dedupe: o boot do main.ts e a antessala pedem /users/me quase juntos.
      // Sem isto saem duas chamadas idênticas em paralelo a cada carga.
      if (inFlight) return inFlight
      inFlight = (async () => {
        try {
          const response = await axios.get('/users/me')
          this.id = response.data.id
          this.name = response.data.name
          this.username = response.data.username
          this.email = response.data.email
          this.profile = response.data.profile
          const raw = response.data.theme
          const t = String(raw === 1 ? 'dark' : raw === 0 ? 'light' : raw || '').toLowerCase()
          this.theme = t === 'dark' ? 'dark' : 'light'

          this.isAdmin = response.data.admin === 'admin'
          this.supportLevel = response.data.support_level ?? null
          this.aiAgentsRole = response.data.ai_agents_role ?? 'none'
          this.twoFactorEnabled = response.data.otp_required_for_login === true
        } catch (error) {
          console.error('Failed to fetch user data', error)
          console.error('Erro na requisição:', error.response?.status, error.response?.data)
        } finally {
          inFlight = null
        }
      })()
      return inFlight
    },

    async changeUserName(newName) {
      if (this.id === null) {
        console.error('User ID is not set')
        return
      }

      try {
        await axios.patch(`/users/${this.id}`, { user: { name: newName } })
        this.name = newName
      } catch (error) {
        console.error('Failed to change user name', error)
      }
    },

    async changeUsername(newUsername) {
      if (this.id === null) {
        console.error('User ID is not set')
        return
      }

      try {
        await axios.patch(`/users/${this.id}`, { user: { username: newUsername } })
        this.username = newUsername
      } catch (error) {
        console.error('Failed to change username', error)
      }
    },

    async changePassword(oldPassword: string, newPassword: string) {
      if (this.id === null) {
        console.error('User ID is not set')
        return
      }

      await axios.post(`/users/${this.id}/change_password`, {
        user: {
          old_password: oldPassword,
          new_password: newPassword,
        },
      })
    },

    async checkIfAdmin() {
      try {
        if (this.isAdmin) {
          return true
        }
        const response = await axios.get('/users/me')
        this.isAdmin = response.data.admin === 1
        return this.isAdmin
      } catch (error) {
        console.error('Failed to check admin status', error)
        return false
      }
    },
  },
})
